/**
 * 审计日志 —— **真的**,来自 hub 的 /audit(只追加,不改不删)。
 *
 * 记的是"谁在什么时候动了什么":注册/轮换/吊销设备、建任务、确认告警。
 * 不记遥测上报 —— 那是每分钟一条的高频流,写进来会把真正该看的动作淹掉。
 */
import { useAudit } from '@/hooks/useHubData';
import { LoadError, NoDataSource } from '@/components/feature/DataSource';
import { timeAgoSec } from '@/lib/format';

const actionLabel: Record<string, { text: string; icon: string; tone: string }> = {
  'device.register': { text: '注册设备', icon: 'ri-add-circle-line', tone: 'text-accent-600' },
  'device.rotate': { text: '轮换令牌', icon: 'ri-refresh-line', tone: 'text-primary-600' },
  'device.revoke': { text: '吊销令牌', icon: 'ri-forbid-line', tone: 'text-red-600' },
  'task.create': { text: '创建任务', icon: 'ri-task-line', tone: 'text-primary-600' },
  'alert.ack': { text: '确认告警', icon: 'ri-check-line', tone: 'text-foreground-600' },
};

export default function AuditLog() {
  const { entries, loading, error, unavailable, refetch } = useAudit(200);

  if (unavailable) {
    return <NoDataSource title="审计未配置" reason={unavailable} phase="给 hub 配上 fleet.db 即可" />;
  }
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-xs font-bold text-foreground-800">审计日志</h3>
          <p className="text-[11px] text-foreground-400 mt-0.5">谁、什么时候、动了什么。只追加,不可修改。</p>
        </div>
        <button onClick={refetch} className="text-[11px] text-foreground-500 hover:text-foreground-700 cursor-pointer whitespace-nowrap">
          <i className={`ri-refresh-line mr-1 ${loading ? 'animate-spin' : ''}`} />刷新
        </button>
      </div>

      {error && <LoadError error={error} onRetry={refetch} />}

      {entries.length === 0 && !loading ? (
        <p className="text-xs text-foreground-400 py-8 text-center">还没有可审计的动作。注册一台设备或建一个任务后这里就有了。</p>
      ) : (
        <ul className="divide-y divide-background-200/60">
          {entries.map((e) => {
            const a = actionLabel[e.action] ?? { text: e.action, icon: 'ri-file-list-line', tone: 'text-foreground-600' };
            return (
              <li key={e.id} className="flex items-start gap-3 py-2.5">
                <span className={`w-7 h-7 shrink-0 rounded-lg bg-background-100 flex items-center justify-center ${a.tone}`}>
                  <i className={`${a.icon} text-sm`} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 text-xs">
                    <span className="font-medium text-foreground-800">{a.text}</span>
                    {e.target && <span className="font-mono text-foreground-500">{e.target}</span>}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-2 text-[10px] text-foreground-400 mt-0.5">
                    <span className="font-mono">{e.actor}</span>
                    <span>{new Date(e.at * 1000).toLocaleString('zh-CN')}</span>
                    <span>({timeAgoSec(e.at)})</span>
                  </div>
                  {e.detail && e.detail !== 'null' && (
                    <pre className="mt-1 text-[10px] font-mono text-foreground-500 bg-background-100 rounded px-2 py-1 overflow-x-auto">{e.detail}</pre>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
